import React from "react";
import classNames from "classnames";

import { roundDecimalsInText } from "../../../util/flagSummaryUtils";
import { getO2, getRR } from "../../../util/rr";
import { ConditionTagList } from "../ConditionTag";
import styles from "./styles.module.scss";

/**
 * Pretty renderer for the `neo_flag_card` content block that seeds a
 * flag-launched Neo conversation (backend: `app/helper/Neo/flag_intro.py`).
 * Shows the flag narrative, colored condition chips, and a "Last vitals" panel
 * instead of a raw JSON dump. Colors/rounding reuse the flag-inbox utilities so
 * the chat matches the rest of the app.
 *
 * Part of the ui2026 workstream (flag -> Neo chat).
 */

export interface NeoFlagCardVitals {
  resp?: string;
  heart?: string;
  bp?: string;
  spo2?: string;
  weight?: string;
  taken_at?: string;
}

export interface NeoFlagCardContentPart {
  type: "neo_flag_card";
  version?: string;
  patient_name?: string;
  room?: string | null;
  dob?: string | null;
  narrative?: string;
  conditions?: string[];
  vitals?: NeoFlagCardVitals | null;
  [key: string]: unknown;
}

const formatTakenAt = (iso?: string): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d
    .toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    .toLowerCase()
    .replace(/\s/g, "");
};

/** Strip a trailing unit the source may already include (e.g. "97%"). */
const stripUnit = (value: string, unit: string): string =>
  value
    .trim()
    .replace(new RegExp(`\\s*${unit}\\s*$`, "i"), "")
    .trim();

/** Trim stored floats like "20.000000" -> "20" / "98.60" -> "98.6". Leaves
 *  non-numeric readings (e.g. "117/78") untouched. */
const tidyNumber = (value: string): string => {
  const s = value.trim();
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (!Number.isNaN(n)) {
      return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(1)));
    }
  }
  return s;
};

interface VitalCellProps {
  label: string;
  value?: string;
  unit?: string;
  danger?: boolean;
}

const VitalCell: React.FC<VitalCellProps> = ({
  label,
  value,
  unit,
  danger,
}) => {
  if (!value) return null;
  return (
    <div className={styles.vitalCell}>
      <span className={styles.vitalLabel}>{label}</span>
      <span
        className={classNames(styles.vitalValue, {
          [styles.vitalDanger]: danger,
        })}
      >
        {value}
        {unit ? <small>{unit}</small> : null}
      </span>
    </div>
  );
};

const NeoFlagCard: React.FC<{ card: NeoFlagCardContentPart }> = ({ card }) => {
  const narrative = (card.narrative || "").trim();
  const paragraphs = narrative
    .split(/\n{2,}|\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const conditions = Array.isArray(card.conditions) ? card.conditions : [];
  const vitals = card.vitals || null;

  const resp = vitals?.resp ? tidyNumber(vitals.resp) : undefined;
  const heart = vitals?.heart ? tidyNumber(vitals.heart) : undefined;
  const bp = vitals?.bp ? tidyNumber(vitals.bp) : undefined;
  const weight = vitals?.weight ? tidyNumber(vitals.weight) : undefined;
  const spo2 = vitals?.spo2
    ? tidyNumber(stripUnit(vitals.spo2, "%"))
    : undefined;
  // Same thresholds as the flag inbox (src/constants/vitalThresholds.ts).
  const respDanger = resp ? getRR(Number(resp)).color === "red" : false;
  const spo2Danger = spo2 ? getO2(Number(spo2)).color === "red" : false;
  const takenAt = formatTakenAt(vitals?.taken_at);

  const hasVitals =
    !!vitals &&
    (vitals.resp || vitals.heart || vitals.bp || vitals.spo2 || vitals.weight);

  return (
    <div className={styles.card}>
      {paragraphs.length > 0 ? (
        <div className={styles.narrative}>
          {paragraphs.map((paragraph, index) => (
            <p key={index}>{roundDecimalsInText(paragraph)}</p>
          ))}
        </div>
      ) : null}

      {conditions.length > 0 ? (
        <ConditionTagList
          conditions={conditions}
          className={styles.conditions}
        />
      ) : null}

      {hasVitals ? (
        <div className={styles.vitalsCard}>
          <div className={styles.vitalsHeader}>
            Last vitals{takenAt ? `, taken ${takenAt}` : ""}
          </div>
          <div className={styles.vitalsGrid}>
            <VitalCell
              label="Resp"
              value={resp}
              unit="br/min"
              danger={respDanger}
            />
            <VitalCell label="Heart" value={heart} unit="bpm" />
            <VitalCell label="BP" value={bp} />
            <VitalCell label="SpO₂" value={spo2} unit="%" danger={spo2Danger} />
            <VitalCell label="Weight" value={weight} unit="lb" />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NeoFlagCard;
